import { WithNativeHTMLProps } from "@kalyzee/kast-web-sdk/web-components";
import React, { ForwardedRef, useCallback, useEffect, useMemo, useRef } from "react";

import { GroupRequestCard, ReactIcons, RequestCardContainer, UserRequestCard } from "@kalyzee/escaet-ui";
import { MeetingRoom, MeetingRoomHighlightingState, MeetingRoomStatus, MeetingSession } from "@kalyzee/kast-web-sdk/websocket";
import { respondToSessionHighlightRequest, responseRoomHighlight } from "../../helpers/meeting";
import { useStandaloneHighlightedMeetingSessions } from "../../hooks/meeting";
import { PopulatedMeetingData } from "../../interfaces/populatedData";
import { MeetingContainerAPI } from "@kalyzee/kast-web-sdk/contexts/meeting/core/container";
import { playNotificationSoundEffect } from "../../helpers/sounds";
import { usePopulatedMeetingData } from "../../hooks/populatedData";

export const useUsersHighlightRequestsList = (meetingContainer: MeetingContainerAPI | undefined) => {
  const { pending: pendingHighlightingSessions } = useStandaloneHighlightedMeetingSessions(meetingContainer);
  const users = useMemo(
    () =>
      pendingHighlightingSessions
        .map((s) =>
          s.user
            ? {
                profilePicture: s.user.avatar,
                firstName: s.user.firstname,
                lastName: s.user.lastname,
                session: s,
              }
            : null
        )
        .filter((u) => (u ? true : false)) as {
        profilePicture: string;
        firstName: string;
        lastName: string;
        session: MeetingSession;
      }[],
    [pendingHighlightingSessions]
  );

  const respond = useCallback(async (session: MeetingSession, accept: boolean) => {
    if (!meetingContainer) return;
    const updated = await respondToSessionHighlightRequest(session.id, accept);
    if (updated) meetingContainer.setSession(updated);
  }, [meetingContainer]);

  const respondAll = useCallback(async (accept: boolean) => {
    const promises: Promise<void>[] = [];
    users.forEach((u) => {
      promises.push(respond(u.session, accept));
    });
    await Promise.all(promises);
  }, [users, respond]);

  return {
    users,
    respond,
    respondAll,
  }
}

export const useGroupsHighlightRequestsList = (meetingContainer: MeetingContainerAPI | undefined) => {
  const populatedMeetingData = usePopulatedMeetingData(meetingContainer);
  const groups = useMemo(() => {
    const ongoing = populatedMeetingData?.rooms.groups.filter((group) => group.room.status === MeetingRoomStatus.Ongoing) ?? [];
    const pendingGroups = ongoing.filter((group) => group.room.highlighting === MeetingRoomHighlightingState.PENDING_REQUEST);
    return pendingGroups;
  }, [populatedMeetingData]);

  const respond = useCallback(async (room: MeetingRoom, accept: boolean) => {
    if (!meetingContainer) return;
    await responseRoomHighlight(room.id, accept);
  }, [meetingContainer]);

  const respondAll = useCallback(async (accept: boolean) => {
    const promises: Promise<void>[] = [];
    groups.forEach((g) => {
      promises.push(respond(g.room, accept));
    });
    await Promise.all(promises);
  }, [groups, respond]);

  return {
    groups,
    respond,
    respondAll,
  }
}

export interface PendingInterventionsListProps extends WithNativeHTMLProps<HTMLDivElement> {
  mode?: 'all' | 'groups_only' | 'users_only';
  meetingContainer?: MeetingContainerAPI;
  className?: string;
  style?: React.CSSProperties;
  onClose?: () => void;
}

export interface PendingInterventionsListRef {}

const HighlightRequestsList = React.forwardRef(
  ({ mode = "all", meetingContainer, className, style, onClose, nativeProps }: PendingInterventionsListProps, forwardRef: ForwardedRef<PendingInterventionsListRef>) => {
    const containerRef = useRef<HTMLDivElement>(null);

    const { users, respond: respondToUser, respondAll: respondToAllUsers } = useUsersHighlightRequestsList(meetingContainer);
    const { groups, respond: respondToGroup, respondAll: respondToAllGroups } = useGroupsHighlightRequestsList(meetingContainer);
    const previousUsersRef = useRef<typeof users>();
    const previousGroupsRef = useRef<typeof groups>();

    // ---------------------------------------------------------------------- //
    // ---------------------------  UTILS ----------------------------- //
    // ---------------------------------------------------------------------- //


    // ---------------------------------------------------------------------- //
    // ---------------------------  EFFECTS ----------------------------- //
    // ---------------------------------------------------------------------- //

    useEffect(() => {
      if (previousUsersRef.current !== undefined) {
        users.forEach((u1) => {
          if (!previousUsersRef.current?.find((u2) => u1.session.id === u2.session.id)) {
            // new user
            playNotificationSoundEffect();
          }
        });
      }
      previousUsersRef.current = users;
    }, [users]);

    useEffect(() => {
      if (previousGroupsRef.current !== undefined) {
        groups.forEach((g1) => {
          if (!previousGroupsRef.current?.find((g2) => g1.id === g2.id)) {
            // new user
            playNotificationSoundEffect();
          }
        });
      }
      previousGroupsRef.current = groups;
    }, [groups]);

    // ---------------------------------------------------------------------- //
    // ---------------------------  RENDER ----------------------------- //
    // ---------------------------------------------------------------------- //


    const classes = [];
    if (className) classes.push(className);

    const renderCards = () => {
      const nodes:JSX.Element[] = [];
      if (mode === "all" || mode === "users_only") {
        nodes.push(...users.map((user) => (
          <UserRequestCard
            key={`user-${user.session.id}`}
            profilePicture={user.profilePicture}
            firstName={user.firstName}
            lastName={user.lastName}
            onAccept={() => respondToUser(user.session, true)}
            onCancel={() => respondToUser(user.session, false)}
          />
        )));
      }
      if (mode === "all" || mode === "groups_only") {
        nodes.push(...groups.map((group) => (
          <GroupRequestCard
            key={`group-${group.id}`}
            name={`Groupe ${group.room.name ?? ''}`}
            onAccept={() => respondToGroup(group.room, true)}
            onCancel={() => respondToGroup(group.room, false)}
          />
        )));
      }
      return nodes;
    }

    return (
      <div {...(nativeProps ?? {})} className={classes.join(" ")} style={style} ref={containerRef}>
        <RequestCardContainer
          title="Demandes d'intervention"
          Icon={ReactIcons.Finger}
          className="h-full w-full"
          onAcceptAll={async () => {
            if (mode === "all" || mode === "users_only")  await respondToAllUsers(true);
            if (mode === "all" || mode === "groups_only")  await respondToAllGroups(true);
          }}
          onCancelAll={async () => {
            if (mode === "all" || mode === "users_only")  await respondToAllUsers(false);
            if (mode === "all" || mode === "groups_only")  await respondToAllGroups(false);
          }}
          onClose={onClose}
          cards={renderCards()}
        />
      </div>
    );
  }
);

export default HighlightRequestsList;
